import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type LoadingState = [
  boolean,
  string,
  number | undefined,
  (loading: boolean) => void,
  (message?: string) => void,
  (percent?: number) => void,
];

const LoadingContext = createContext<LoadingState>(null!);

export function createLoadingState(): LoadingState {
  const [loadingCount, setLoadingCount] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingPercent, setLoadingPercent] = useState<number | undefined>(undefined);
  return useMemo(
    () => [
      loadingCount > 0,
      loadingMessage,
      loadingPercent,
      (loading: boolean) => setLoadingCount((count) => (loading ? count + 1 : count - 1)),
      (message?: string) => setLoadingMessage(message ?? ''),
      (percent?: number) => setLoadingPercent(percent),
    ],
    [loadingCount, loadingMessage, loadingPercent],
  );
}

export function LoadingProvider({ children, loadingState }: React.PropsWithChildren<{ loadingState?: LoadingState }>) {
  const context = loadingState ?? createLoadingState();
  return <LoadingContext.Provider value={context}>{children}</LoadingContext.Provider>;
}

export function LoadingContributor({
  children,
  loading,
  message,
  percent,
}: React.PropsWithChildren<{ loading: boolean; message?: string; percent?: number }>) {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('LoadingContext not found');
  }
  const [localLoading, setLocalLoading] = useState(false);
  useEffect(() => {
    if (localLoading !== loading) {
      context[3](loading);
    }
    setLocalLoading(loading);
  }, [loading, localLoading]);
  useEffect(() => {
    context[4](message);
  }, [message]);
  useEffect(() => {
    context[5](percent);
  }, [percent]);
  return <>{children}</>;
}
